import { Comment } from '@cube3/state/types';
import { MediaPlayer } from '@cube3/ui/src/previews/VideoPreview/MediaPlayer';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { createContext } from 'react';
import { generatePath, matchPath, useHistory, useLocation } from 'react-router';
import {
  ReviewRouteParams,
  urlStructureReviewLinkAsset,
  urlStructureReviewLinkComment
} from '../../../routing/routingPaths';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';

interface ReviewToolContextType {
  drawingBarToggled: boolean;
  activeComment?: Comment;
  drawnSvg: string;
  player: MediaPlayer; // mediaPlayer class instance
  playing: boolean;
  setPlaying?: (playing) => void;
  playerRef?: (playerProps) => void;
  setActiveComment?: (comment: Comment) => void;
  setDrawnSvg: (svg) => void;
  toggleDrawingBar: (toggle?: boolean) => void;
  // social media mockups
  activeTemplate?: string;
  setTemplate?: (string) => void;
}
export const ReviewToolContext = createContext<ReviewToolContextType>(null);

export const ReviewToolContextProvider = (props) => {
  const [drawingBarToggled, toggleDrawingBar] = useState(false);
  const [drawnSvg, setDrawnSvg] = useState(undefined);
  const [activeComment, setActiveComment] = useState(undefined);
  const [playerRef, setPlayerRef] = useState(undefined);
  const [playing, setPlaying] = useState(false);
  const [activeTemplate, setTemplate] = useState(undefined);

  const location = useLocation();
  const history = useHistory();
  const matchedParams = matchPath<ReviewRouteParams>(
    location.pathname,
    urlStructureReviewLinkAsset
  )?.params;
  const commentFromUrl = matchPath<ReviewRouteParams>(
    location.pathname,
    urlStructureReviewLinkComment
  )?.params?.commentId;

  const { resource: commentResource, status: fetchCommentStatus } =
    useResource__ALPHA({
      resourceType: commentFromUrl ? 'comment' : undefined,
      resourceId: commentFromUrl
    });

  /** The parent of the commentId obtained from the url */
  const threadId = commentResource && commentResource.relationships?.thread?.id;
  const { resource: thread } = useResource__ALPHA({
    resourceType: threadId ? 'comment' : undefined,
    resourceId: threadId
  });

  const updateUrl = useCallback(
    (commentId: string) => {
      if (!matchedParams) return;
      const newUrl = commentId
        ? generatePath(urlStructureReviewLinkComment, {
          token: matchedParams.token,
          assetId: matchedParams.assetId,
          commentId
        })
        : generatePath(urlStructureReviewLinkAsset, {
          token: matchedParams.token,
          assetId: matchedParams.assetId
        });
      history.push(newUrl);
    },
    [matchedParams, commentFromUrl]
  );

  const onClickComment = useCallback(
    (comment) => {
      setActiveComment(comment);
      if (comment) {
        updateUrl(comment.id);
      }
    },
    [updateUrl]
  );

  // set active comment from url
  // if the comment id from url is a reply(child comment), should set it's parent as the active comment
  useEffect(() => {
    if (commentFromUrl && commentResource) {
      setActiveComment(thread || commentResource);
    }
  }, [commentFromUrl, thread, commentResource]);

  // Clear the active comment if fetching the comment from url fails
  useEffect(() => {
    if (commentFromUrl && fetchCommentStatus === statuses.FAILED) {
      updateUrl(null);
    }
  }, [commentFromUrl, fetchCommentStatus]);


  // deselect a comment when clicking outside of it
  React.useEffect(() => {
    const deselect = (ev) => {
      console.info("DEBUG deselect callback")
      let ct = ev.target || ev.changedTouches.item(0).target;
      while (ct) {
        if (ct.dataset && ct.dataset.set === 'preventDeselect') {
          console.info("DEBUG deselect callback blocked by", ct)
          return;
        }
        ct = ct.parentNode;
      }
      console.info("DEBUG deselect callback not blocked")

      setActiveComment(undefined);
    };
    addEventListener('pointerdown', deselect);
    return () =>
      removeEventListener('pointerdown', deselect);
  }, [setActiveComment]);

  const context = useMemo(() => {
    return {
      drawingBarToggled,
      drawnSvg,
      activeComment,
      player: playerRef,
      playing,
      activeTemplate,
      setTemplate,
      setPlaying,
      playerRef: setPlayerRef,
      toggleDrawingBar: (toggle?) =>
        toggleDrawingBar((prev) => (toggle === undefined ? !prev : toggle)),
      setDrawnSvg,
      setActiveComment: onClickComment
    };
  }, [
    drawingBarToggled,
    drawnSvg,
    activeComment,
    playerRef,
    playing,
    activeTemplate,
    setTemplate,
    setPlaying,
    setPlayerRef,
    toggleDrawingBar,
    setDrawnSvg,
    setActiveComment
  ]);

  return (
    <ReviewToolContext.Provider value={context}>
      {props.children}
    </ReviewToolContext.Provider>
  );
};

export const useReviewToolContext = () => {
  return useContext(ReviewToolContext);
};

export const ReviewAssetViewerContext = createContext<{
  timestamp: number;
  setTimestamp?: (time: number) => void;
}>(null);
export const ReviewAssetViewerContextProvider = (props) => {
  const [timestamp, setTimestamp] = useState(undefined);

  const context = useMemo(() => {
    return {
      timestamp,
      setTimestamp
    };
  }, [timestamp, setTimestamp]);

  return (
    <ReviewAssetViewerContext.Provider value={context}>
      {props.children}
    </ReviewAssetViewerContext.Provider>
  );
};
